import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui';
import { ROUTES } from '@/constants';

interface ErrorPageProps {
  title?: string;
  message?: string;
  showHomeButton?: boolean;
  showBackButton?: boolean;
  onRetry?: () => void;
}

export function ErrorPage({
  title,
  message,
  showHomeButton = true,
  showBackButton = true,
  onRetry,
}: ErrorPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const displayTitle = title ?? t('common.error');
  const displayMessage = message ?? t('errors.generic');

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-destructive/5 to-destructive/10 p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader className="space-y-4">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl">{displayTitle}</CardTitle>
          <CardDescription className="text-base">{displayMessage}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2">
            {showBackButton && (
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  navigate(-1);
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('common.back')}
              </Button>
            )}
            <Button variant="default" className="flex-1" onClick={handleRetry}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common.retry')}
            </Button>
          </div>

          {showHomeButton && (
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => {
                navigate(ROUTES.home);
              }}
            >
              <Home className="mr-2 h-4 w-4" />
              {t('navigation.home')}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
